import { z } from 'zod';

/**
 * Campaign Configuration Schema
 *
 * Defines how to launch a specific campaign on a host machine.
 * Used across realtime-api (storage) and host-frontend (consumption).
 *
 * Maps directly to PlayjectorService launch message format.
 */

// =============================================================================
// Input Filter Schemas
// =============================================================================

/**
 * Keyboard filter schema
 * Array of filter objects, each specifying a key code and optional modifiers
 */
const keyboardFilterSchema = z.object({
  key: z.number(),                    // Key code (e.g., 65 for 'A')
  modifiers: z.array(z.string()),     // Modifier keys (e.g., ['ctrl', 'alt'])
});

/**
 * Mouse filter schema
 * Boolean flags where false = blocked, true = allowed
 */
const mouseFilterSchema = z.object({
  leftClick: z.boolean(),
  rightClick: z.boolean(),
  middleClick: z.boolean(),
  move: z.boolean(),
  scroll: z.boolean(),
  xButton1: z.boolean(),
  xButton2: z.boolean(),
});

/**
 * Gamepad filter schema
 * Boolean flags where false = blocked, true = allowed
 */
const gamepadFilterSchema = z.object({
  dPadUp: z.boolean(),
  dPadDown: z.boolean(),
  dPadLeft: z.boolean(),
  dPadRight: z.boolean(),
  start: z.boolean(),
  back: z.boolean(),
  leftThumb: z.boolean(),
  rightThumb: z.boolean(),
  leftShoulder: z.boolean(),
  rightShoulder: z.boolean(),
  A: z.boolean(),
  B: z.boolean(),
  X: z.boolean(),
  Y: z.boolean(),
  leftTrigger: z.boolean(),
  rightTrigger: z.boolean(),
  sThumbLX: z.boolean(),
  sThumbLY: z.boolean(),
  sThumbRX: z.boolean(),
  sThumbRY: z.boolean(),
});

// =============================================================================
// Launch Package Schemas
// =============================================================================

/**
 * Base launch package schema - common fields for all launcher types
 */
const launchPackageBaseSchema = z.object({
  launcher: z.string(),
  readyWaitTimeMs: z.number(),
  filters: z.object({
    gamepad: gamepadFilterSchema.optional(),
    keyboard: z.array(keyboardFilterSchema).optional(),
    mouse: mouseFilterSchema.optional(),
  }).optional(),
});

/**
 * Android Emulator launcher package schema
 */
export const launchPackageAndroidSchema = launchPackageBaseSchema.extend({
  launcher: z.literal('android'),
  package: z.string(),
});

/**
 * Steam launcher package schema
 */
export const launchPackageSteamSchema = launchPackageBaseSchema.extend({
  launcher: z.literal('steam'),
  launchId: z.string(),      // Steam AppID
  arguments: z.string(),     // Launch arguments
});

/**
 * Epic Games launcher package schema
 */
export const launchPackageEpicSchema = launchPackageBaseSchema.extend({
  launcher: z.literal('epic'),
  itemId: z.string(),        // Epic item ID
  arguments: z.string(),     // Launch arguments
  token: z.string().optional(),         // Auth token
  username: z.string().optional(),      // Epic username
  userId: z.string().optional(),        // Epic user ID
  sandboxId: z.string().optional(),     // Epic sandbox ID
  filePath: z.string().optional(),      // Path to the game executable
});

export const launchPackageStandaloneSchema = launchPackageBaseSchema.extend({
  launcher: z.literal('standalone'),
  exePath: z.string(),    // Path to EXE to launch
  exeSha256: z.string(),  // EXE SHA256
  arguments: z.string(),  // Launch arguments
});

/**
 * Union of all launch package schemas
 */
export const launchPackageSchema = z.discriminatedUnion('launcher', [
  launchPackageAndroidSchema,
  launchPackageSteamSchema,
  launchPackageEpicSchema,
  launchPackageStandaloneSchema,
]);

// =============================================================================
// Cooldown Schemas
// =============================================================================

/**
 * When the matchmaking cooldown timer starts.
 * - 'onGameLaunched'      — start when launch macro completes (gameReady)
 * - 'onFirstPlayerJoined' — start when the first player connects (e.g. Hitman contract clock)
 * - 'never'               — disabled (default)
 *
 * Host-side authority. The realtime-api only persists/serves this value.
 */
export const cooldownStartTriggerSchema = z.enum([
  'onGameLaunched',
  'onFirstPlayerJoined',
  'never',
]);
export type CooldownStartTrigger = z.infer<typeof cooldownStartTriggerSchema>;

// =============================================================================
// Campaign Configuration Schema
// =============================================================================

export const campaignConfigSchema = z.object({
  // Campaign metadata
  campaignId: z.string(),            // Campaign slug (e.g., 'shades', 'borderlands3')
  displayName: z.string(),           // Human-readable campaign name
  active: z.boolean(),               // Whether this campaign is currently active

  // Host edition for this campaign
  edition: z.enum(['standard', 'pavilion']),

  // Launch package - contains all launcher-specific configuration
  launchPackage: launchPackageSchema,

  // Macro that runs when the game is ready and launched (optional)
  onLaunchMacro: z.array(z.string()).optional().default([]),

  // Macro that runs right before player join (optional)
  onJoinMacro: z.array(z.string()).optional().default([]),

  // Macro that runs when all players leave and restartOnDisconnect is false (optional)
  onExitMacro: z.array(z.string()).optional().default([]),

  // Macro that runs on a 30s interval while no players are connected (optional)
  onInactiveMacro: z.array(z.string()).optional().default([]),

  // Whether to restart game when all players disconnect (Ad Mode only)
  restartOnDisconnect: z.boolean().optional().default(true),

  // Matchmaking cooldown configuration (host-side authority).
  // When set, the host suppresses matchmakeability for cooldownPeriodMs after the trigger event.
  // Used for games whose own progression has a real-world wait (e.g. Hitman 30-min contract clock).
  // Both fields are optional; consumers default `cooldownStartTrigger` to `'never'` and
  // `cooldownPeriodMs` to `0` at read time so existing campaigns remain unchanged.
  cooldownStartTrigger: cooldownStartTriggerSchema.optional(),
  cooldownPeriodMs: z.number().int().min(0).optional(),
});

// =============================================================================
// Inferred Types
// =============================================================================

/**
 * Launch package types inferred from schemas
 */
export type LaunchPackageAndroid = z.infer<typeof launchPackageAndroidSchema>;
export type LaunchPackageSteam = z.infer<typeof launchPackageSteamSchema>;
export type LaunchPackageEpic = z.infer<typeof launchPackageEpicSchema>;
export type LaunchPackage = z.infer<typeof launchPackageSchema>;

/**
 * Campaign Configuration Type
 * Inferred from the Zod schema for type safety
 */
export type CampaignConfig = z.infer<typeof campaignConfigSchema>;

/**
 * API Response for fetching a single campaign
 */
export const campaignApiResponseSchema = z.object({
  success: z.boolean(),
  campaign: campaignConfigSchema.optional(),
  error: z.string().optional(),
});

export type CampaignApiResponse = z.infer<typeof campaignApiResponseSchema>;

/**
 * API Response for fetching all active campaigns
 */
export const campaignsListApiResponseSchema = z.object({
  success: z.boolean(),
  campaigns: z.array(campaignConfigSchema).optional(),
  count: z.number().optional(),
  error: z.string().optional(),
});

export type CampaignsListApiResponse = z.infer<typeof campaignsListApiResponseSchema>;

